<template>
  <div class="og-select" :class="{ 'disabled': disabled }">
    <s-popper
      :arrow="arrow"
      :class="$slots.trigger ? '' : 'og-select__control'"
      :disabled="disabled"
      key="select-control"
      @closed="_onDropdownClosed"
      @opened="_onDropdownOpened"
      :placement="placement"
      popup-class="og-select-dropdown"
      tab-index="0"
      v-model="isDropdownOpen"
      :animation-frame="showTags && multiple"
      ref="floatingRef"
    >
      <template v-if="$slots.trigger">
        <slot name="trigger" :is-dropdown-open="isDropdownOpen"/>
      </template>
      <template v-else>
        <div class="og-select__content" key="select-content-container">
          <template v-if="(ajaxAdapter && ajaxAdapter.isLoadingValue) || !arrayValue.length">
            <div class="og-select__content__placeholder">{{ defaultPlaceHolder }}</div>
          </template>

          <template v-else-if="multiple">
            <div class="og-select__content__selection">
              <template v-if="$slots.result">
                <slot name="result" :value="arrayValue"/>
              </template>
              <template v-else>
                {{ $t('extra.selected') }}: {{ arrayValue.length }}
              </template>
            </div>
          </template>

          <template v-else>
            <div class="og-select__content__selection">
              <template v-if="$slots.result">
                <slot name="result" :value="arrayValue[0]"/>
              </template>
              <template v-else>
                {{ getDisplayValue(arrayValue[0]) }}
              </template>
            </div>
          </template>
        </div>

        <div class="og-select__buttons">
          <template v-if="nullable && hasValue">
            <div class="og-select__buttons__button" @click="clear">
              <span class="icon-cross3"/>
            </div>
          </template>

          <div class="og-select__buttons__button">
            <dropdown-icon :class="[{ 'og-select-arrow--flipped': isDropdownOpen }]"/>
          </div>
        </div>
      </template>

      <template #popup>
        <div :class="arrow ? 'dropdown-menu' : 'og-select-dropdown__container'">
          <template v-if="searchable">
            <div class="form-group-feedback-right">
              <input
                class="og-select-search"
                @click="stopPropagation"
                @input="_onFilterInput"
                @keyup.enter="onInputEnter"
                :placeholder="searchPlaceholder"
                v-focus
                :value="filter"
              >
              <div class="form-control-feedback form-control-feedback-lg">
                <i class="icon-search4 opacity-50"/>
              </div>
            </div>
          </template>
          <template v-if="$slots.header">
            <slot name="header" :value="this"/>
          </template>
          <div class="og-select-results" v-scroll-end="_onScrollEnd">
            <template v-if="showDefault && filter">
              <div class="og-select-result d-flex justify-content-between" @click="selectDefault">
                <div class="text-break">{{ filter }}</div>
                <div class="badge">{{ $t('actions.choose') }}</div>
              </div>
            </template>
            <template v-for="group in filteredGroups" :key="group.key">
              <div class="og-select-group">
                <template v-if="group.title">
                  <div class="og-select-group__title">{{ group.title }}</div>
                </template>
                <template v-for="(item, index) in group.items" :key="index">
                  <select-item
                    :is-selected-fn="isSelected"
                    :item="item"
                    @select="toggleItemSelection"
                  >
                    <template v-if="$slots.item">
                      <slot name="item" :value="item"/>
                    </template>
                    <template v-else>
                      {{ getDisplayValue(item) }}
                    </template>
                  </select-item>
                </template>
              </div>
            </template>

            <template v-if="isAjaxLoadingList">
              <div class="og-select-loader">
                <div/>
                <div/>
                <div/>
                <div/>
              </div>
            </template>

            <template v-if="!isAjaxLoadingList && !filteredGroups.length">
              <template v-if="$slots.empty">
                <slot name="empty"/>
              </template>
              <template v-else>
                <div class="p-2 text-center">{{ emptyTextPlaceHolder }}</div>
              </template>
            </template>

            <template v-if="$slots.footer">
              <slot name="footer" :value="this"/>
            </template>
          </div>
        </div>
      </template>
    </s-popper>

    <template v-if="displayTags">
      <div class="og-select-tags" key="select-tags">
        <template v-for="(item, index) in arrayValue" :key="item.key">
          <div class="og-select-tag">
            <div class="og-select-tag__content">
              <template v-if="$slots.tag">
                <slot name="tag" :value="item" :index="index"/>
              </template>
              <template v-else>
                {{ getDisplayValue(item) }}
              </template>
            </div>
            <template v-if="!$slots.removeTag">
              <div class="og-select-tag__remove" @click="deselectItem(item)">&times;</div>
            </template>
            <template v-if="$slots.removeTag">
              <slot name="removeTag" :on-click="deselectItem" :item="item" :index="index"/>
            </template>
          </div>
        </template>
      </div>
    </template>

    <template v-if="ajaxAdapter && ajaxAdapter.isLoadingValue">
      <div class="og-select-value-loader">
        <i class="icon-spinner9 spinner mr-2"/>
      </div>
    </template>
  </div>
</template>

<script>
  import DropdownIcon from '@/components/icons/dropdown';
  import { focus, scrollEnd } from '@/directives';
  import { getSize, makeIterator } from '@/utils/data';
  import Fuse from 'fuse.js';
  import debounce from 'lodash/debounce';
  import get from 'lodash/get';
  import { toRaw } from 'vue';
  import SelectItem from '@/components/select/select-item';

  const VALUE_KEY = '$key';
  const VALUE_KEY_FIELD = '$keyField';
  const VALUE_VALUE = '$value';

  const DEFAULT_PLACEHOLDER_TEXT = 'extra.select_item';
  const DEFAULT_MISSING_TEXT = 'extra.invalid_value';
  const DEFAULT_EMPTY_TEXT = 'extra.empty_list';

  export default {
    name: 'OgSelect',

    components: { DropdownIcon, SelectItem },

    directives: { scrollEnd, focus },

    props: {
      modelValue: {
        type: null,
        default: null,
      },
      items: {
        type: [Array, Object],
        default: () => [],
      },
      placeholder: {
        type: String,
        default() {
          return DEFAULT_PLACEHOLDER_TEXT;
        },
      },
      keyField: {
        type: String,
        default: VALUE_VALUE,
      },
      textField: {
        type: String,
        default: VALUE_VALUE,
      },
      matchBy: {
        type: String,
        default: VALUE_KEY_FIELD,
      },
      missingText: {
        type: String,
        default() {
          return DEFAULT_MISSING_TEXT;
        },
      },
      emptyText: {
        type: String,
        default() {
          return DEFAULT_EMPTY_TEXT;
        },
      },
      disabled: {
        type: Boolean,
        default: null,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      nullable: {
        type: Boolean,
        default: false,
      },
      searchable: {
        type: Boolean,
        default: false,
      },
      searchPlaceholder: {
        type: String,
        default: null,
      },
      ajaxAdapter: {
        type: Object,
        default: null,
      },
      showTags: {
        type: Boolean,
        default: true,
      },
      dropdownWidth: {
        type: [Number, Function],
        default: null,
      },
      emptyItem: {
        type: null,
        default: null,
      },
      placement: {
        type: String,
        default: 'bottom',
      },
      arrow: {
        type: Boolean,
        default: false,
      },
      showDefault: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['selectDefault', 'update:modelValue', 'update:items', 'select', 'keyupEnter'],

    data() {
      return {
        isDropdownOpen: false,
        internalValue: null,
        filter: '',
        removeValueUpdateWatcher: null,
      };
    },

    computed: {
      matchByField() {
        if (this.matchBy === VALUE_KEY_FIELD) {
          return this.keyField;
        }

        return this.matchBy;
      },

      matchDirectField() {
        return this.matchByField !== VALUE_KEY && this.matchByField !== VALUE_VALUE;
      },

      groups() {
        const groups = [];

        if (this.emptyItem) {
          groups.push({
            key: 'empty-item-group',
            title: null,
            items: this._wrapItems([this.emptyItem]),
            visible: true,
            ajax: false,
          });
        }

        if (getSize(this.items) > 0) {
          groups.push({
            key: 'item-group',
            title: null,
            items: this._wrapItems(this.items),
            visible: this.ajaxAdapter === null,
            ajax: false,
          });
        }

        if (this.ajaxAdapter?.sourceItems && getSize(this.ajaxAdapter.sourceItems) > 0) {
          groups.push({
            key: 'ajax-group-source',
            title: null,
            items: this._wrapItems(this.ajaxAdapter.sourceItems, true),
            visible: false,
            ajax: false,
          });
        }

        if (this.ajaxAdapter?.listItems && getSize(this.ajaxAdapter.listItems) > 0) {
          groups.push({
            key: 'ajax-group',
            title: null,
            items: this._wrapItems(this.ajaxAdapter.listItems, true),
            visible: true,
            ajax: true,
          });
        }

        return groups;
      },

      filteredGroups() {
        if (this.ajaxAdapter) {
          return this.groups.filter(group => group.visible && group.items.length);
        }

        if (!this.filter.trim()) {
          return this.groups.filter(group => group.visible && group.items.length);
        }

        const groups = [];
        // eslint-disable-next-line no-useless-assignment
        let _filtered = null;

        for (const group of this.groups) {
          if (!group.visible) {
            continue;
          }

          const fuse = new Fuse(group.items, {
            keys: ['text'],
            threshold: 0.0,
            ignoreLocation: true,
            getFn(obj, path) {
              const searchKeys = Array.isArray(path) ? path : [path];

              for (const key of searchKeys) {
                const val = get(obj, key);

                if (typeof val === 'string') {
                  return val;
                }
              }

              return '';
            },
          });
          _filtered = fuse.search(this.filter || '').map(result => result.item);

          if (_filtered.length) {
            groups.push(Object.assign({}, group, { items: _filtered }));
          }
        }

        return groups;
      },

      hasValue() {
        if (this.multiple) {
          return this.modelValue && this.modelValue.length > 0;
        }

        return Boolean(this.modelValue);
      },

      isAjaxLoadingList() {
        if (!this.ajaxAdapter) {
          return false;
        }

        return this.ajaxAdapter.isLoadingList;
      },

      defaultPlaceHolder() {
        return this.placeholder === DEFAULT_PLACEHOLDER_TEXT ? this.$t(DEFAULT_PLACEHOLDER_TEXT) : this.placeholder;
      },

      emptyTextPlaceHolder() {
        return this.emptyText === DEFAULT_EMPTY_TEXT ? this.$t(DEFAULT_EMPTY_TEXT) : this.emptyText;
      },

      arrayValue() {
        let valueArray = [];
        if (this.internalValue !== null) {
          valueArray = this.multiple ? this.internalValue : [this.internalValue];
        }

        return valueArray;
      },

      displayTags() {
        if (this.ajaxAdapter && this.ajaxAdapter.isLoadingValue) {
          return false;
        }

        return this.showTags && this.multiple && this.arrayValue.length > 0;
      },
    },

    created() {
      this.removeValueUpdateWatcher = this.$watch(() => {
        // Register dependency tracking
        [this.modelValue, this.items, (this.ajaxAdapter && this.ajaxAdapter.sourceItems), this.textField, this.keyField]
          .filter(x => Boolean(x));

        return Date.now();
      }, () => {
        this._updateInternalValue(this.modelValue);
      }, { immediate: true });
    },

    beforeUnmount() {
      this.removeValueUpdateWatcher();
    },

    methods: {
      getDisplayValue(internalItem) {
        if (typeof internalItem.text === 'object') {
          return JSON.stringify(internalItem.text);
        }

        return internalItem.text;
      },

      stopPropagation(event) {
        event.stopPropagation();
      },

      toggleItemSelection(wrappedItem) {
        if (this.isSelected(wrappedItem)) {
          this.deselectItem(wrappedItem);
        } else {
          this.selectItem(wrappedItem);
        }

        if (this.multiple) {
          this.updateDropdownWidth();
        }
      },

      selectItem(wrappedItem) {
        if (wrappedItem.missing) {
          return;
        }

        if (wrappedItem.ajax) {
          this.addItemToList(wrappedItem);
        }

        if (this.multiple) {
          const value = Array.isArray(this.internalValue) ? this.internalValue : [];

          if (this.keyField === VALUE_VALUE && this.matchDirectField) {
            this.$emit('update:modelValue', value.map(selection => selection.item).concat([wrappedItem.item]));
          } else {
            this.$emit('update:modelValue', value.map(selection => selection.key).concat([wrappedItem.key]));
          }
        } else {
          if (this.keyField === VALUE_VALUE && this.matchDirectField) {
            this.$emit('update:modelValue', wrappedItem.item);
          } else {
            this.$emit('update:modelValue', wrappedItem.key);
          }
        }

        if (this.isDropdownOpen && !this.multiple) {
          this.isDropdownOpen = false;
        }
      },

      deselectItem(wrappedItem) {
        if (!this.multiple) {
          this.isDropdownOpen = false;
          return;
        }

        const value = toRaw(Array.isArray(this.internalValue) ? this.internalValue : []);
        if (this.keyField === VALUE_VALUE && this.matchDirectField) {
          this.$emit('update:modelValue', value
            .filter(item => item.key !== wrappedItem.key)
            .map(selection => {
              if (selection.item) {
                return selection.item;
              }
              return { [this.matchByField]: selection.key };
            }),
          );
        } else {
          this.$emit('update:modelValue', this.internalValue
            .filter(item => item.key !== wrappedItem.key)
            .map(item => item.key),
          );
        }
      },

      isSelected(wrappedItem) {
        if (this.internalValue === null) {
          return false;
        }

        if (this.multiple) {
          return Boolean(this.internalValue.find(item => item.key === wrappedItem.key));
        }

        return this.internalValue && this.internalValue.key === wrappedItem.key;
      },

      clear(event = null) {
        if (event) {
          event.stopPropagation();
        }

        this.$emit('update:modelValue', null);

        if (this.isDropdownOpen) {
          this.isDropdownOpen = false;
        }
      },

      addItemToList(wrappedItem) {
        if (this.keyField === VALUE_KEY && !this.matchDirectField) {
          this.$emit('update:items', Object.assign({}, this.items, { [wrappedItem.key]: wrappedItem.item }));
          return;
        }

        if (this.keyField === VALUE_VALUE && !this.matchDirectField) {
          if (this.items.indexOf(wrappedItem.key) >= 0) {
            return;
          }

          this.$emit('update:items', this.items.concat([wrappedItem.key]));
          return;
        }

        const matches = this._matchSelectedItems([wrappedItem.key]).filter(match => !match.missing);
        if (matches.length) {
          return;
        }

        this.$emit('update:items', this.items.concat([wrappedItem.item]));
        this.ajaxAdapter?.addItem(wrappedItem.item);
      },

      setFilterValue(value) {
        this.filter = value;
        this._triggerFilter();
      },

      openDropdown() {
        this.isDropdownOpen = true;
      },

      closeDropdown() {
        this.isDropdownOpen = false;
      },

      _getItemKey(item, index) {
        if (this.matchDirectField && typeof item !== 'object') {
          throw new Error(`Unsupported key field "${this.matchByField}" with simple array items.`);
        }

        switch (this.matchByField) {
          case VALUE_VALUE:
            return item;
          case VALUE_KEY:
            return index;
          default:
            return item[this.matchByField];
        }
      },

      _getItemText(item, index) {
        if (this.textField !== VALUE_VALUE && this.textField !== VALUE_KEY && typeof item !== 'object') {
          throw new Error(`Unsupported text field "${this.textField}" with simple array items.`);
        }

        switch (this.textField) {
          case VALUE_VALUE:
            return item;
          case VALUE_KEY:
            return index;
          default:
            return get(item, this.textField);
        }
      },

      _wrapItems(items, ajax = false) {
        const list = [];

        for (const { key, value } of makeIterator(items)) {
          list.push({
            key: this._getItemKey(value, key),
            text: this._getItemText(value, key),
            item: value,
            missing: false,
            ajax: ajax,
          });
        }

        return list;
      },

      _updateInternalValue(value) {
        if ((!this.emptyItem && value === null) || value === undefined) {
          this.internalValue = null;
          this.ajaxAdapter?.updateValue(null);
          return;
        }

        const matches = this._matchSelectedItems(this.multiple ? value : [value]);

        if (this.emptyItem) {
          if (matches.length && matches[0].missing) {
            this.internalValue = this.groups[0].items[0];
          }
        }

        this.internalValue = this.multiple ? matches : matches[0];
        this.ajaxAdapter?.updateValue(this.internalValue);
      },

      _matchSelectedItems(values) {
        const list = this.groups.filter(group => !group.ajax);
        const groupLength = list.length;
        const matchMap = new Map();
        // eslint-disable-next-line no-useless-assignment
        let _keyIndex = -1;
        let _matched = false;
        // eslint-disable-next-line no-useless-assignment
        let _itemLength = 0;
        // eslint-disable-next-line no-useless-assignment
        let _group = null;
        // eslint-disable-next-line no-useless-assignment
        let _item = null;

        const keyList = values.map(key => {
          if (key && typeof key === 'object' && this.matchDirectField) {
            matchMap.set(key[this.matchByField], null);
            return key[this.matchByField];
          }

          matchMap.set(key, null);
          return key;
        });

        for (let i = 0; i < groupLength; i++) {
          _group = list[i];
          _itemLength = _group.items.length;

          for (let j = 0; j < _itemLength; j++) {
            _item = _group.items[j];
            _keyIndex = keyList.indexOf(_item.key);

            if (_keyIndex >= 0) {
              _matched = true;
              matchMap.set(keyList[_keyIndex], _item);
            }

            if (!this.multiple && _matched) {
              return [matchMap.values().next().value];
            }
          }
        }

        const result = [];
        matchMap.forEach(((value, key) => {
          if (value === null) {
            result.push({
              key: key,
              text: this.missingText === DEFAULT_MISSING_TEXT ? this.$t(DEFAULT_MISSING_TEXT) : this.missingText,
              item: null,
              missing: true,
              ajax: false,
            });
          } else {
            result.push(value);
          }
        }));

        return result;
      },

      _onDropdownOpened() {
        this.updateDropdownWidth();

        if (this.ajaxAdapter) {
          this.ajaxAdapter.loadNextPage();
        }

        this.isDropdownOpen = true;
      },

      async updateDropdownWidth() {
        await this.$nextTick();

        if (this.$refs.floatingRef?.floating) {
          if (typeof this.dropdownWidth === 'function') {
            this.$refs.floatingRef.floating.style.width = `${this.dropdownWidth()}px`;

            return;
          }

          if (this.dropdownWidth) {
            this.$refs.floatingRef.floating.style.width = `${this.dropdownWidth}px`;
          } else {
            this.$refs.floatingRef.floating.style.width = `${this.$el.clientWidth}px`;
          }
        }
      },

      _onDropdownClosed() {
        this.isDropdownOpen = false;
        this.setFilterValue('');
      },

      _onScrollEnd() {
        this.ajaxAdapter?.loadNextPage();
      },

      _onFilterInput(e) {
        this.setFilterValue(e.target.value);
      },

      _triggerFilter: debounce(function () {
        this.ajaxAdapter?.search(this.filter);
      }, 150),

      selectDefault() {
        this.$emit('selectDefault', this.filter);
      },

      onInputEnter() {
        if (this.filter) {
          this.$emit('keyupEnter', this.filter, this);
          this.isDropdownOpen = false;
        }
      },
    },
  };
</script>
